import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { DropdownItem } from '@atlaskit/dropdown-menu';
import Spinner from '@atlaskit/spinner/spinner';

import { useCommentsDataState } from '@confluence/comments-data';
import { withFlags, type WithFlagsProps } from '@confluence/flags';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useCommentService } from '@confluence/content-types-bridge-comments';

import { useCommentBulkResolve } from '../hooks/useCommentBulkResolve';
import { computeResolvedCommentIds } from '../helper/commentHelpers';
import { useCommentBulkOperationsState } from '../hooks/useCommentBulkOperations';
import { useCommentBulkOperationsFlags, RESOLVE } from '../hooks/useCommentBulkOperationsFlags';

const i18n = defineMessages({
	markAllAsResolved: {
		id: 'comments-panel.comment-bulk-actions.mark-all-as-resolved',
		defaultMessage: 'Mark all as resolved',
		description:
			'The label for a button used to resolve all comments associated with the content being viewed',
	},
});

export const CommentBulkResolve = ({ flags }: WithFlagsProps) => {
	const { formatMessage } = useIntl();
	const { retryCountRef, ...commentBulkActionFlags } = useCommentBulkOperationsFlags(
		RESOLVE,
		flags,
	);

	const commentBulkResolve = useCommentBulkResolve(commentBulkActionFlags);

	const { commentService } = useCommentService();

	const { commentsDataMap } = useCommentsDataState();
	const { isBulkResolveInProgress } = useCommentBulkOperationsState();
	const pageMode = useGetPageMode();
	const includeDrafts = pageMode !== PageMode.VIEW;
	const unresolvedCommentCount = computeResolvedCommentIds(commentsDataMap, includeDrafts).length;

	return (
		<DropdownItem
			elemBefore={isBulkResolveInProgress && <Spinner size="small" />}
			isDisabled={isBulkResolveInProgress || unresolvedCommentCount === 0}
			onClick={() => {
				retryCountRef.current = 0;

				void commentBulkResolve();
				commentService?.onResolveComment('all');
			}}
		>
			{formatMessage(i18n.markAllAsResolved)}
		</DropdownItem>
	);
};

export const CommentBulkResolveWithFlags = withFlags(CommentBulkResolve);
